import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import ReviewedBy from "@/components/ReviewedBy";
import AdSlot from "@/components/AdSlot";
import SectorResources from "@/components/SectorResources";
import useNoindexOnNotFound from "@/hooks/useNoindexOnNotFound";
import { Briefcase, FileText, Award, BookOpen, ChevronRight } from "lucide-react";

const AD_SLOT_SECTOR = process.env.REACT_APP_AD_SLOT_SECTOR || "";

const SITE_URL = "https://hireds.in";

function useSectorSeo(hub) {
  useEffect(() => {
    if (!hub) return;
    // Remove the homepage-only extended-schema FAQPage if it was loaded via SPA navigation
    // (prevents "Duplicate FAQPage" GSC warning if user lands on / then navigates to /sectors/*)
    const extended = document.getElementById("extended-schema-jsonld");
    if (extended) extended.remove();
    const oldTitle = document.title;
    document.title = `${hub.title} | hireds.in`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    const oldDesc = metaDesc.content;
    metaDesc.content = (hub.intro || hub.title).slice(0, 160);
    let canonical = document.querySelector('link[rel="canonical"]');
    const oldCanonical = canonical ? canonical.href : null;
    if (canonical) canonical.href = `${SITE_URL}/sectors/${hub.slug}`;
    let ogUrl = document.querySelector('meta[property="og:url"]');
    const oldOg = ogUrl ? ogUrl.content : null;
    if (ogUrl) ogUrl.content = `${SITE_URL}/sectors/${hub.slug}`;

    // JSON-LD: CollectionPage + ItemList of jobs + FAQPage + BreadcrumbList
    const itemList = {
      "@type": "ItemList",
      "name": hub.h1,
      "numberOfItems": hub.total_jobs || hub.jobs.length,
      "itemListElement": hub.jobs.slice(0, 10).map((j, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `${SITE_URL}/jobs/${j.slug}`,
        "name": j.title,
      })),
    };
    const faqPage = hub.faqs?.length
      ? {
          "@type": "FAQPage",
          "mainEntity": hub.faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }
      : null;
    const collectionPage = {
      "@type": "CollectionPage",
      "name": hub.title,
      "description": hub.intro,
      "url": `${SITE_URL}/sectors/${hub.slug}`,
      "isPartOf": { "@type": "WebSite", "url": SITE_URL, "name": "hireds.in" },
      "about": { "@type": "Thing", "name": `${hub.name} Government Jobs in India` },
      "reviewedBy": { "@type": "Person", "name": "Dileshwar Vitthale", "jobTitle": "Chief Editor, hireds.in" },
    };
    const breadcrumb = {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Sectors", "item": `${SITE_URL}/sectors` },
        { "@type": "ListItem", "position": 3, "name": hub.name, "item": `${SITE_URL}/sectors/${hub.slug}` },
      ],
    };
    const graph = [collectionPage, breadcrumb, itemList];
    if (faqPage) graph.push(faqPage);
    const data = { "@context": "https://schema.org", "@graph": graph };
    let el = document.getElementById("sector-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "sector-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);

    return () => {
      document.title = oldTitle;
      if (oldDesc !== undefined) metaDesc.content = oldDesc;
      if (oldCanonical !== null && canonical) canonical.href = oldCanonical;
      if (oldOg !== null && ogUrl) ogUrl.content = oldOg;
      const cur = document.getElementById("sector-jsonld");
      if (cur) cur.remove();
    };
  }, [hub]);
}

export default function SectorHub() {
  const { slug } = useParams();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/sectors/${slug}`).then((r) => setHub(r.data)).catch(() => setHub(false)).finally(() => setLoading(false));
  }, [slug]);
  useSectorSeo(hub && typeof hub === "object" ? hub : null);
  useNoindexOnNotFound(hub === false);

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-12 text-slate-500">Loading…</div>;
  if (!hub) return <div className="max-w-6xl mx-auto px-4 py-12 text-slate-500" data-testid="sector-not-found">This sector page is no longer available. Browse <Link to="/sectors" className="text-orange-600 underline">all sectors</Link>.</div>;

  return (
    <article className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid={`sector-hub-${hub.slug}`}>
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Sectors", to: "/sectors" },
          { label: hub.name },
        ]}
      />

      <header className="mb-6">
        <div className="inline-block text-xs uppercase tracking-wider font-bold text-orange-600 bg-orange-50 border border-orange-200 rounded px-2 py-1">
          {hub.name} Hub
        </div>
        <h1
          className="text-3xl md:text-5xl font-extrabold tracking-tight mt-2 text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
        >
          {hub.h1}
        </h1>
        <p className="mt-3 text-base text-slate-700 leading-relaxed max-w-4xl">{hub.intro}</p>
        <ReviewedBy date={new Date().toISOString()} />
      </header>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat icon={Briefcase} label="Open Vacancies" value={hub.total_jobs} testId="sector-stat-jobs" />
        <Stat icon={FileText} label="Admit Cards" value={hub.admit_cards.length} testId="sector-stat-admits" />
        <Stat icon={Award} label="Latest Results" value={hub.results.length} testId="sector-stat-results" />
        <Stat icon={BookOpen} label="Guides" value={hub.articles.length} testId="sector-stat-articles" />
      </div>

      <AdSlot slot={AD_SLOT_SECTOR} />

      {/* Exams in this sector */}
      {hub.exams?.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-lg p-5 md:p-6 mb-8" data-testid="sector-exams">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            Major {hub.name} Exams Covered Here
          </h2>
          <div className="flex flex-wrap gap-2">
            {hub.exams.map((e) => (
              <span key={e} className="text-sm bg-slate-100 border border-slate-200 text-slate-800 rounded-full px-3 py-1.5 font-medium">
                {e}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Open Jobs */}
      {hub.jobs.length > 0 && (
        <section className="mb-10" data-testid="sector-jobs">
          <div className="flex items-end justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              Latest Open {hub.name} Vacancies
            </h2>
            <Link to={`/all-india-jobs?department=${hub.slug}`} className="text-sm text-orange-600 hover:text-orange-700 font-semibold inline-flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {hub.jobs.map((j) => (
              <Link
                key={j.slug}
                to={`/jobs/${j.slug}`}
                data-testid={`sector-job-${j.slug}`}
                className="block bg-white border border-slate-200 hover:border-orange-400 hover:shadow-sm rounded-lg p-4 transition-colors"
              >
                <div className="text-sm font-bold text-slate-900 line-clamp-2">{j.title}</div>
                <div className="text-xs text-slate-600 mt-1">{j.organization}</div>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                  {j.total_posts ? <span><span className="font-semibold">{j.total_posts}</span> Posts</span> : null}
                  {j.last_date ? <span>Last Date: <span className="font-semibold">{fmtDate(j.last_date)}</span></span> : null}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Admit Cards & Results — two columns */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        {hub.admit_cards.length > 0 && (
          <section data-testid="sector-admits">
            <h2 className="text-lg md:text-xl font-extrabold tracking-tight text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              {hub.name} Admit Cards
            </h2>
            <ul className="space-y-2">
              {hub.admit_cards.map((a) => (
                <li key={a.slug}>
                  <Link to={`/admit-cards/${a.slug}`} className="block bg-white border border-slate-200 hover:border-orange-400 rounded-lg px-4 py-3 transition-colors">
                    <div className="text-sm font-semibold text-slate-900 line-clamp-1">{a.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{a.organization}{a.exam_date ? ` · Exam: ${fmtDate(a.exam_date)}` : ""}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        {hub.results.length > 0 && (
          <section data-testid="sector-results">
            <h2 className="text-lg md:text-xl font-extrabold tracking-tight text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              {hub.name} Results
            </h2>
            <ul className="space-y-2">
              {hub.results.map((r) => (
                <li key={r.slug}>
                  <Link to={`/results/${r.slug}`} className="block bg-white border border-slate-200 hover:border-orange-400 rounded-lg px-4 py-3 transition-colors">
                    <div className="text-sm font-semibold text-slate-900 line-clamp-1">{r.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{r.organization}{r.result_date ? ` · Declared: ${fmtDate(r.result_date)}` : ""}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Guides / Articles */}
      {hub.articles.length > 0 && (
        <section className="mb-10" data-testid="sector-articles">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            {hub.name} Prep Guides & Articles
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {hub.articles.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="block bg-white border border-slate-200 hover:border-orange-400 hover:shadow-sm rounded-lg p-4 transition-colors"
              >
                <div className="text-xs uppercase tracking-wider font-bold text-orange-600">{a.category || "Guide"}</div>
                <div className="text-sm font-bold text-slate-900 mt-1 line-clamp-2">{a.title}</div>
                {a.excerpt && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{a.excerpt}</div>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Resources: official syllabus & previous-year papers (curated .gov.in links) */}
      <SectorResources resources={hub.resources} sectorName={hub.name} />

      {/* FAQ */}
      {hub.faqs?.length > 0 && (
        <section className="mb-10" data-testid="sector-faqs">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            Frequently Asked Questions about {hub.name} Jobs
          </h2>
          <div className="space-y-2">
            {hub.faqs.map((f, i) => (
              <details key={i} className="group bg-white border border-slate-200 rounded-md p-4 hover:border-orange-300 transition-colors" data-testid={`sector-faq-${i}`}>
                <summary className="cursor-pointer min-h-11 inline-flex items-start font-semibold text-slate-900 list-none w-full">
                  <span className="flex-1 pr-2">{f.q}</span>
                  <span className="ml-2 text-orange-600 group-open:rotate-45 transition-transform text-xl leading-none shrink-0" aria-hidden="true">+</span>
                </summary>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>{value}</div>
    </div>
  );
}
